import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { LocalAiLeadHeadcountRow } from "@/api/local-ai-lead-api"

import { Users } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { DashboardSectionCard } from "@/features/admin-dashboard/components/admin-dashboard-shared"

type LeadHeadcountCardProps = {
  rows: LocalAiLeadHeadcountRow[]
  isSaving: boolean
  onSave: (groupValue: string, employees: number) => void
}

export function LeadHeadcountCard({ rows, isSaving, onSave }: LeadHeadcountCardProps) {
  const { t } = useTranslation()
  const [drafts, setDrafts] = useState<Record<string, string>>({})

  return (
    <DashboardSectionCard
      icon={Users}
      id="local-ai-lead-headcount"
      title={t("localAiLeadPage.headcount.title", "Employees and adoption")}
      description={t(
        "localAiLeadPage.headcount.description",
        "Headcount is not in the platform. You keep the figure for your own group. It does not change the cost."
      )}
    >
      {rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {t(
            "localAiLeadPage.headcount.empty",
            "No group is available for your assigned users yet."
          )}
        </p>
      ) : (
        <ul className="space-y-3">
          {rows.map((row) => {
            const draft =
              drafts[row.groupValue] ?? (row.employees != null ? String(row.employees) : "")
            const parsed = Number(draft)
            const updatedAt = row.updatedAt ? new Date(row.updatedAt) : null
            const age =
              updatedAt && !Number.isNaN(updatedAt.getTime())
                ? Date.now() - updatedAt.getTime()
                : null
            const stale = row.employees != null && (age === null || age >= 90 * 24 * 60 * 60 * 1000)
            const canSave =
              Number.isInteger(parsed) && parsed > 0 && (parsed !== row.employees || stale)
            return (
              <li key={row.groupValue} className="flex flex-wrap items-end justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{row.groupValue}</p>
                  <p className="text-muted-foreground text-xs">
                    {row.adoption == null
                      ? t(
                          "localAiLeadPage.headcount.notReported",
                          "Adoption appears once you enter employees"
                        )
                      : t(
                          "localAiLeadPage.headcount.adoption",
                          "{{pct}}% adoption · {{users}} active users",
                          {
                            pct: row.adoption.toFixed(1),
                            users: row.activeUsers,
                          }
                        )}
                  </p>
                  {row.employees != null && row.activeUsers > row.employees ? (
                    <p className="text-warning text-xs" role="status">
                      {t(
                        "localAiLeadPage.headcount.exceedsActive",
                        "Active users exceed this headcount. Please review the figure."
                      )}
                    </p>
                  ) : null}
                  {stale ? (
                    <p className="text-warning text-xs" role="status">
                      {t(
                        "localAiLeadPage.headcount.reconfirm",
                        "Please reconfirm this headcount; it is over 90 days old or has no update date."
                      )}
                    </p>
                  ) : null}
                  {!stale && updatedAt ? (
                    <p className="text-muted-foreground text-xs">
                      {t("localAiLeadPage.headcount.lastUpdated", "Last updated {{date}}", {
                        date: updatedAt.toLocaleDateString(),
                      })}
                    </p>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    className="w-28"
                    inputMode="numeric"
                    min={1}
                    step={1}
                    type="number"
                    value={draft}
                    aria-label={t("localAiLeadPage.headcount.employees", "Employees at {{group}}", {
                      group: row.groupValue,
                    })}
                    onChange={(event) =>
                      setDrafts((current) => ({ ...current, [row.groupValue]: event.target.value }))
                    }
                  />
                  <Button
                    disabled={!canSave || isSaving}
                    size="sm"
                    type="button"
                    onClick={() => onSave(row.groupValue, parsed)}
                  >
                    {t("common.save", "Save")}
                  </Button>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </DashboardSectionCard>
  )
}
